import React, { useContext, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Row } from 'react-table';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import DataTable from '../../../DataTable/datatable.component';
import DateColumn from '../../../DataTable/datatable.datecolumn';
import NoFilter from '../../../DataTable/datatable.nofilter';
import SelectFilter from '../../../DataTable/datatable.selectfilter';
import { LabResult } from '../../../Types/LabResult';
import useAlert from '../../../Alerts/useAlert';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import ManageLabResultsDialog from '../../../Dialogs/ManageLabResultsDialog';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';

export interface LabResultsSectionProps {
    isRailCarSite?: boolean,
    showSampleGrade?: boolean
}

const LabResultsSection = memo(function LabResultsSection({ isRailCarSite = false, showSampleGrade = false }: LabResultsSectionProps) {

    const { t } = useTranslation();
    const { lotDetail: { summary: { lotNumber }, excludeLabResults: excludeSampleIds, includeLabResults: includeSampleIds, labResults }, getLotDetail } = useContext(LotDetailContext);

    const tableColumns = [
        DateColumn({ header: t('LotDetails.LabResults.SampleTime'), accessor: 'sampleTimeUtc' }),
        ...isRailCarSite
            ? [
                DateColumn({ header: t('LotDetails.LabResults.AuthorisedTime'), accessor: 'authorisedTimeUtc' }),
                {
                    Header: t('LotDetails.LabResults.BatchName'),
                    accessor: 'batchName',
                    Filter: SelectFilter
                },
                {
                    Header: t('LotDetails.LabResults.UnitArea'),
                    accessor: 'unitArea',
                    Filter: SelectFilter
                }
            ]
            : [],
        {
            Header: t('LotDetails.LabResults.Quality'),
            accessor: 'quality',
            Filter: SelectFilter
        },
        {
            Header: t('LotDetails.LabResults.Analysis'),
            accessor: 'analysis',
            Filter: SelectFilter
        },
        {
            Header: t('LotDetails.LabResults.Component'),
            accessor: 'component',
            Filter: SelectFilter
        },
        {
            Header: t('LotDetails.LabResults.SamplingPoint'),
            accessor: 'samplingPoint',
            Filter: SelectFilter
        },
        {
            Header: t('LotDetails.LabResults.SMSampleId'),
            accessor: 'smSampleId',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.LabResults.Value'),
            accessor: 'value',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.LabResults.Units'),
            accessor: 'units',
            Filter: NoFilter
        },
        ...showSampleGrade ? [{
            Header: t('Grade'),
            accessor: 'grade',
            Filter: SelectFilter
        }] : [],
    ];

    const getRowProps = (row: Row<LabResult>) => {
        const classNames: string[] = [];
        if (!row.original.usedInCalc) {
            classNames.push("unused");
        }

        if (row.original.isOffspec) {
            classNames.push("offspec");
        }

        if (classNames.length > 0) {
            return { className: classNames.join(' ') };
        }

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'sampleTimeUtc',
                desc: false
            },
            {
                id: 'quality',
                desc: false
            },
            {
                id: 'collection',
                desc: false
            }
        ],
        pageSize: 50
    };

    const manageLabResults = <ManageLabResults lotNumber={lotNumber} labResults={labResults} includeSampleIds={includeSampleIds} excludeSampleIds={excludeSampleIds} getLotDetail={getLotDetail} />;

    return labResults.length > 0
        ? <DataTable tableColumns={tableColumns} tableData={labResults} initialState={initialState} autoResetPage={false} getRowProps={getRowProps} CellWrapper={LabResultsRowWrapper}>
            {manageLabResults}
        </DataTable>
        : <>
            <p>{t<string>('CommonResponses.NoData')}</p>
            {manageLabResults}
        </>
        ;
});

interface ManageLabResultsProps {
    lotNumber: string,
    labResults: LabResult[],
    includeSampleIds: string[],
    excludeSampleIds: string[],
    getLotDetail: Function
}

function ManageLabResults({ lotNumber, labResults, includeSampleIds, excludeSampleIds, getLotDetail }: ManageLabResultsProps) {

    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [showManageLabResultsDialog, setShowManageLabResultsDialog] = useState(false);

    const handleManageLabResultsSave = (comment: string, include: string[], exclude: string[]) => {

        fetchDataAuthenticated(`/api/v1/lot/${lotNumber}/sampleOverrides`, 'PUT', JSON.stringify({ comment: comment, include: include, exclude: exclude }))
            .then(async response => {
                if (response.ok) {
                    getLotDetail();
                    setShowManageLabResultsDialog(false);
                } else {
                    throw new Error("Failed to add the comment to the lot");
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            });
    }

    return (
        <>
            <Button variant="primary" onClick={() => setShowManageLabResultsDialog(true)}>Manage Lab Results</Button>
            <ManageLabResultsDialog showDialog={showManageLabResultsDialog} closeDialog={() => setShowManageLabResultsDialog(false)} onSave={handleManageLabResultsSave} labResults={labResults} includeSampleIds={includeSampleIds} excludeSampleIds={excludeSampleIds} />
        </>
    );
}

interface LabResultsTableWrapperProps {
    cell: Cell<LabResult>,
    children: React.JSX.Element
}

function LabResultsRowWrapper({ cell, children }: LabResultsTableWrapperProps) {
    const { t } = useTranslation();
    if (cell === undefined || cell.row.original.usedInCalc) {
        return children;
    } else {
        return (
            <OverlayTrigger overlay={<Tooltip>{t("LotDetails.LabResults.UnusedInCalculation")}</Tooltip>} popperConfig={{}}>
                {children}
            </OverlayTrigger>
        );
    }
}


export default LabResultsSection;